/*
 * Copyright (c) 2024-2026. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */
import React, {useState} from 'react'
import {useTranslation} from 'react-i18next'
import clsx from 'clsx'

import {PackageCategory} from 'types/enums/PackageCategory'
import UiButton, {UiButtonSize, UiButtonStyle} from 'components/Ui/UiButton'
import {ChevronUpIcon} from "@heroicons/react/24/solid";
import {ChevronDownIcon} from "@heroicons/react/24/outline";
import Dates from 'utils/Dates'
import {useTypedSelector} from "helpers/reducers";
import type {PackageCategoryEntity} from "types/entities/PackageCategoryEntity";

interface OfferEventCategoryProps {
    eventCategory: PackageCategoryEntity // Using any as the type is complex and not fully defined in the snippet
    isCategoryNotSelected: (id: PackageCategory | string) => boolean | null
    getShortNameFromId: (id: PackageCategory | string) => string
    renderCategoryContent: (eventCategory: PackageCategoryEntity, disabled: boolean) => React.ReactNode
}

const OfferEventCategory: React.FC<OfferEventCategoryProps> = ({
    eventCategory,
    isCategoryNotSelected,
    getShortNameFromId,
    renderCategoryContent,
}) => {
    const { t } = useTranslation()
    const notSelected = isCategoryNotSelected(eventCategory.id)
    const [isDetailsOpen, setIsDetailsOpen] = useState(false)

    const bookingFilter = useTypedSelector((state) => state.bookingState.bookingProcess.filter)
    const isPackageCategoryDisabled = (() => {
        if ((!eventCategory.extra.releaseDate  && !eventCategory.extra.expiryDate) || !bookingFilter.date) {
            return false
        }
		const filterDate = new Date(bookingFilter.date);
        const dateDiff = Dates.daysDifference(new Date(eventCategory.extra.releaseDate), filterDate)

		if(eventCategory.extra.expiryDate) {
			const dateDiffExpiry = Dates.daysDifference(eventCategory.extra.expiryDate, filterDate)
			if(dateDiffExpiry < 0) {
				return false;
			}
		}
        return dateDiff > 0
    })()

    return (
        <li
            className={clsx(
                'OfferEventCategory col-span-1 flex flex-col rounded-lg bg-white dark:bg-gray-900 shadow',
                notSelected === true && 'hidden',
                notSelected === false && 'sm:col-span-2! md:col-span-4! bg-gray-50 dark:bg-gray-800'
            )}
        >
            <div
                className={clsx(
                    'flex w-full items-center justify-between space-x-6 px-3 py-2.5 xl:px-4 xl:py-3 border-b border-gray-300 dark:border-gray-700'
                )}
            >
                <div className={clsx('flex-1')}>
                    <div className={'flex flex-col items-start gap-1'}>
                        <h3 className={clsx('truncate text-sm font-bold text-gray-900 dark:text-gray-100')}>
                            {eventCategory.title}
                        </h3>
                        {eventCategory.extra?.releaseDate && (
                            <span className={clsx('text-[10px] font-bold text-blue-600 dark:text-blue-400 uppercase')}>
                                {t('New')}
                            </span>
                        )}
                    </div>
                </div>
                <div className={'hidden bg-purple-400'} />
                <div className={clsx('flex flex-col items-end gap-1')}>
                    <p
                        className={clsx(
                            'size-10 shrink-0 rounded-full flex items-center justify-center uppercase font-bold text-gray-900 dark:text-gray-100',
                            `bg-${eventCategory.color}`
                        )}
                    >
                        {getShortNameFromId(eventCategory.id)}
                    </p>
                    {eventCategory.extra?.releaseDate && (
                        <span className={clsx('text-[10px] text-gray-500 whitespace-nowrap')}>
                            {t('from')} {Dates.date(new Date(eventCategory.extra.releaseDate))}
                        </span>
                    )}
                    {eventCategory.extra?.expiryDate && (
                        <span className={clsx('text-[10px] text-gray-500 whitespace-nowrap')}>
                            {t('to')} {Dates.date(new Date(eventCategory.extra.expiryDate))}
                        </span>
                    )}
                </div>
            </div>

            {eventCategory.categoryInfo && (
                <div className={clsx('flex flex-col w-full py-2.5 xl:py-3 px-2')}>
                    <div
                        className={clsx(
                            'transition-all duration-300 ease-in-out overflow-hidden',
                            isDetailsOpen ? 'max-h-250 opacity-100' : 'max-h-0 opacity-0'
                        )}
                    >
                        <ol
                            className={clsx(
                                'grid grid-cols-1 justify-center ml-4 pl-3 xl:pl-4 list-disc marker:text-arbo-red gap-x-3 xl:gap-x-4 text-sm text-gray-800 dark:text-gray-200'
                            )}
                        >
                            {eventCategory.categoryInfo.infoEventPackage?.values.map(
                                (valueText: string, index: number) => (
                                    <li key={index}>{valueText}</li>
                                )
                            )}
                        </ol>
                    </div>

                    <UiButton
                        size={UiButtonSize.Small}
                        style={UiButtonStyle.Whitesmoke}
                        onClick={() => setIsDetailsOpen(!isDetailsOpen)}
                    >
                        {isDetailsOpen ? (
                            <span className={'flex flex-row justify-between'}>
                                {t('Hide package details')} <ChevronUpIcon className={'size-4'} />
                            </span>
                        ) : (
                            <span className={'flex flex-row justify-between'}>
                                {t('Show package details')} <ChevronDownIcon className={'size-4'} />
                            </span>
                        )}
                    </UiButton>
                </div>
            )}

            {(notSelected === false || notSelected === null) &&
                renderCategoryContent(eventCategory, isPackageCategoryDisabled)}
        </li>
    )
}

export default OfferEventCategory
